/**
 * RequisitionPickerModal Component
 *
 * Searches requisitions and hands the picked one to the caller — the entry point of
 * the requisition automation dry-runs.
 */
import { useQuery } from '@apollo/client/react';
import { useState } from 'react';
import { Icon, Input, Loader, Modal, useDebounce } from '@insio/ui-toolkit';
import { formatMoney } from '@insio/decimal';
import { RequisitionsPickerDocument } from '@/graphql';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';

const PICKER_PAGE_SIZE = 8;

export interface RequisitionPickerModalProps {
  isOpen: boolean;
  /** Closes the modal without picking a requisition. */
  onClose: () => void;
  /** Receives the picked requisition; the modal closes right after. */
  onSelect: (requisitionId: string) => void;
  title: string;
  description?: string;
  testId?: string;
}

export function RequisitionPickerModal({
  isOpen,
  onClose,
  onSelect,
  title,
  description,
  testId
}: RequisitionPickerModalProps) {
  const t = useFeatureTranslation('systemSettings.automation.requisitionPicker');

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  const { data, loading: isSearching } = useQuery(RequisitionsPickerDocument, {
    variables: {
      filter: { search: debouncedSearch || undefined },
      first: PICKER_PAGE_SIZE
    },
    fetchPolicy: 'cache-and-network',
    skip: !isOpen
  });

  const requisitions = data?.requisitionsConnection?.edges?.map(edge => edge.node) ?? [];

  const handleSelect = (requisitionId: string) => {
    onSelect(requisitionId);
    onClose();
  };

  return (
    <Modal open={isOpen} onClose={onClose} size='sm' testId={testId}>
      <Modal.Header label={title} description={description} onClose={onClose} variant='white' />
      <Modal.Content className='h-auto! min-h-0 px-lg'>
        <div className='relative'>
          <Icon
            name='magnifying-glass'
            className='absolute left-3 top-1/2 -translate-y-1/2 w-4! h-4! text-foreground-muted pointer-events-none'
            aria-hidden='true'
          />
          <Input
            placeholder={t('searchPlaceholder')}
            value={search}
            onChange={event => setSearch(event.target.value)}
            inputMode='search'
            className='pl-xl'
            testId='requisition-picker-search'
          />
        </div>

        {isSearching && requisitions.length === 0 ? (
          <Loader.Section />
        ) : requisitions.length === 0 ? (
          <p className='text-sm text-foreground-muted text-center py-md'>{t('noResults')}</p>
        ) : (
          <div>
            {requisitions.map(requisition => (
              <button
                key={requisition.id}
                type='button'
                onClick={() => handleSelect(requisition.id)}
                data-testid={`requisition-picker-select-${requisition.id}`}
                className='w-full flex items-center gap-sm py-sm px-sm text-left rounded-md border-b border-border-base last:border-0 cursor-pointer transition-colors hover:bg-background-muted'
              >
                <Icon
                  name='file-lines'
                  className='h-5 w-4 text-foreground-muted shrink-0'
                  aria-hidden='true'
                />
                <span className='flex-1 min-w-0'>
                  <span className='block text-sm font-semibold text-foreground-base truncate'>
                    {requisition.number}
                    {requisition.title ? ` · ${requisition.title}` : ''}
                  </span>
                  {requisition.requester && (
                    <span className='block text-xs text-foreground-muted truncate'>
                      {requisition.requester.displayName}
                    </span>
                  )}
                </span>
                {requisition.estimatedTotalAmountNoVat != null && requisition.currencyCode && (
                  <span className='shrink-0 text-xs font-medium text-foreground-base whitespace-nowrap'>
                    {formatMoney(requisition.estimatedTotalAmountNoVat, {
                      currencyCode: requisition.currencyCode
                    })}
                  </span>
                )}
                <Icon
                  name='chevron-down'
                  rotate={270}
                  className='w-4 h-4 text-foreground-disabled shrink-0'
                  aria-hidden='true'
                />
              </button>
            ))}
          </div>
        )}
      </Modal.Content>
    </Modal>
  );
}
